import { useCallback, useRef, useState } from 'react';
import {
  type TCTemplateRecord,
  type WorkflowTriggerInputMappingInstruction,
} from 'src/api/generated';
import {
  useCreateWorkflowMutation,
  useCreateWorkflowTriggerMutation,
} from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import { useAlert } from 'src/contexts/alert-context';
import { type TemplateDefinition } from '../utils/templates/template-definition.type';
import type { PendingQuickAutomationAttempt } from '../types/quick-automation.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import ErrorReporter from 'src/monitoring/errorReporter';

interface UseCreateAutomationParams {
  /** Full template selected in the quick-automation modal. */
  selectedTemplate: TCTemplateRecord | null;
  /** Validated workflow definition copied from the selected template. */
  templateDefinition?: TemplateDefinition;
  /** User-facing workflow name. */
  workflowName: string;
  /** Builds the final trigger mapping at confirmation time. */
  buildInstructions: () => WorkflowTriggerInputMappingInstruction[];
  /** Called after both workflow and trigger creation succeed. */
  onCreated?: () => void;
  /** Closes the modal after successful creation. */
  onClose: () => void;
}

/**
 * Creates a workflow from a template and safely retries trigger attachment.
 */
export const useCreateAutomationFromTemplate = ({
  selectedTemplate,
  templateDefinition,
  workflowName,
  buildInstructions,
  onCreated,
  onClose,
}: UseCreateAutomationParams) => {
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const createWorkflow = useCreateWorkflowMutation();
  const createTrigger = useCreateWorkflowTriggerMutation();
  const [creating, setCreating] = useState(false);
  const [pendingAttempt, setPendingAttempt] =
    useState<PendingQuickAutomationAttempt | null>(null);
  const creatingRef = useRef(false);

  const onConfirm = useCallback(async () => {
    if (creatingRef.current) {
      return;
    }
    creatingRef.current = true;
    setCreating(true);

    try {
      let attempt = pendingAttempt;

      if (!attempt) {
        if (!selectedTemplate || !workflowName.trim()) {
          return;
        }
        if (!templateDefinition) {
          showAlert(
            t('automations.quickHandoff.errors.invalidDefinition'),
            'error'
          );
          return;
        }
        if (!selectedTemplate.trigger?.id) {
          showAlert(
            t('automations.quickHandoff.errors.missingTrigger'),
            'error'
          );
          return;
        }

        const { outputSchema, inputParams, ...restDefinition } =
          templateDefinition;
        const instructions = buildInstructions();
        let workflowResponse;

        try {
          workflowResponse = await createWorkflow.mutateAsync({
            metadata: {
              name: workflowName.trim(),
              description: selectedTemplate.description,
            },
            definition: restDefinition,
          });
        } catch (error) {
          ErrorReporter.sendException(error);
          showAlert(
            t('automations.quickHandoff.errors.workflowCreate'),
            'error'
          );
          return;
        }

        const workflowId = workflowResponse.metadata.workflowId;
        if (!workflowId) {
          const error = new Error('Workflow response is missing workflowId');
          ErrorReporter.sendException(error);
          showAlert(
            t('automations.quickHandoff.errors.workflowCreate'),
            'error'
          );
          return;
        }

        attempt = {
          workflowId,
          version: workflowResponse.draftVersion.version,
          triggerId: selectedTemplate.trigger.id,
          instructions,
        };
        setPendingAttempt(attempt);
      }

      try {
        await createTrigger.mutateAsync({
          workflowId: attempt.workflowId,
          body: {
            version: attempt.version,
            triggerId: attempt.triggerId,
            inputMapping: {
              mappingVersion: 0,
              instructions: attempt.instructions,
            },
          },
        });
      } catch (error) {
        ErrorReporter.sendException(error);
        showAlert(t('automations.quickHandoff.errors.triggerCreate'), 'error');
        return;
      }

      setPendingAttempt(null);
      showAlert(
        t('automations.quickHandoff.alerts.created', {
          workflowId: attempt.workflowId,
          version: attempt.version,
          triggerId: attempt.triggerId,
        }),
        'success'
      );
      onCreated?.();
      onClose();
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  }, [
    pendingAttempt,
    selectedTemplate,
    workflowName,
    templateDefinition,
    showAlert,
    buildInstructions,
    createWorkflow,
    createTrigger,
    onCreated,
    onClose,
    t,
  ]);

  return {
    onConfirm,
    creating,
    pendingAttempt,
    resetPendingAttempt: () => setPendingAttempt(null),
  };
};
